import { MineralObject } from "./MineralObject";
import { SilicateObject } from "./SilicateObject";

/**
 *
 * @param {Object} suffixObj of Portuguese suffixes from masculine to feminine, used in Sections/fracture
 * @param {String} targetString in Portuguese whose suffix has to be changed
 * @returns
 */

function changeSuffix(suffixObj, targetString) {
  const objKeys = Object.keys(suffixObj);
  const stringToBeConverted = objKeys.reduce((total, k) => {
    let regexEl = new RegExp(`${k}\\b`, "g");
    return (total = total.map((el) => el.replace(regexEl, suffixObj[k])));
  }, targetString);
  return stringToBeConverted;
}

function changePtFemSuffix(suffixObj, targetString) {
  const objKeys = Object.keys(suffixObj);
  const stringToBeConverted = objKeys.reduce((total, k) => {
    let regexEl = new RegExp(`${k}\\b`, "g");
    return targetString.replace(regexEl, suffixObj[k]);
  }, targetString);
  return stringToBeConverted;
}

export default {
  getPageTitle(context) {
    const currentLanguage = this.state.language.currentLanguage;
    context.commit("getPageTitle", currentLanguage);
  },
  /**
   *
   * @returns {Array} with mineral names in the user language
   */
  async getMineralNames(context) {
    const currentLanguage = this.state.language.currentLanguage;
    let mineralNames;

    if (currentLanguage === "pt") {
      mineralNames = await this.state.minerals.mineralDatabase
        .slice()
        .map((el) => el.pt_name)
        .sort();
      return context.commit("getMineralNames", mineralNames);
    }

    mineralNames = await this.state.minerals.mineralDatabase
      .slice()
      .map((el) => el.name)
      .sort();
    context.commit("getMineralNames", mineralNames);
  },
  async getFilterOptions(context) {
    context.commit("getFilterError", false);
    context.commit("addQuery", []);
    context.commit("getFilterResults", []);

    const currentLanguage = this.state.language.currentLanguage;
    // Select Options
    const translate = this.state.language.translate;
    const suffixFix = this.state.language.suffixFix;
    let allSelect = this.state.minerals.allSelectOptions;
    let selectOptions;

    if (currentLanguage === "pt") {
      selectOptions = await Object.entries(allSelect).map((so) => {
        return {
          classId: so[0].replace(/\s/g, ""),
          classText: translate[so[0]],
          categories:
            so[0] === "fracture"
              ? so[1].map((c) => {
                  return {
                    categoryId: c,
                    categoryText: changePtFemSuffix(suffixFix, translate[c]),
                  };
                })
              : so[1].map((c) => {
                  return {
                    categoryId: c,
                    categoryText: translate[c],
                  };
                }),
        };
      });
    }
    if (currentLanguage === "en") {
      selectOptions = await Object.entries(allSelect).map((so) => {
        return {
          classId: so[0].replace(/\s/g, ""),
          classText: so[0],
          categories: so[1].map((c) => {
            return {
              categoryId: c,
              categoryText: c,
            };
          }),
        };
      });
    }

    context.commit("getFilterOptions", selectOptions);
  },
  /**
   * @returns {Array} with key-values of sections in current language and english
   */
  async getSelectOptions(context) {
    const currentLanguage = this.state.language.currentLanguage;
    // Select Options
    const translate = this.state.language.translate;
    const suffixFix = this.state.language.suffixFix;
    let allSelect = this.state.minerals.allSelectOptions;
    let selectOptions;

    if (currentLanguage === "pt") {
      const selectKeys = Object.keys(allSelect);
      const selectValues = Object.values(allSelect);
      const ptSelectKeys = await selectKeys.map((k) => translate[k]);
      const ptSelectValues = await selectValues.map((v) =>
        v.map((val) => translate[val]),
      );
      selectOptions = await ptSelectKeys.map((k, i) => {
        if (selectKeys[i] === "fracture") {
          const tobeModified = changeSuffix(suffixFix, ptSelectValues[i]);
          return [
            [k, tobeModified],
            [selectKeys[i], selectValues[i]],
          ];
        } else {
          return [
            [k, ptSelectValues[i]],
            [selectKeys[i], selectValues[i]],
          ];
        }
      });
    }
    if (currentLanguage === "en") {
      selectOptions = await Object.entries(allSelect).map((so) => {
        return [
          [so[0], so[1]],
          [so[0], so[1]],
        ];
      });
    }
    context.commit("getSelectOptions", selectOptions);
  },
  /**
   * @returns {Object} of all Minerals in the appropriate language
   */
  async getAllMinerals(context) {
    context.commit("displayNoMatch", false);
    const currentLanguage = this.state.language.currentLanguage;
    const dictionary = this.state.language.translate;
    const results = await this.state.minerals.mineralDatabase.map(
      (min) => new MineralObject(currentLanguage, dictionary, min),
    );

    context.commit("getAllMinerals", results);
  },
  /**
   *
   * @param {String} payload of letter typed by user in the input
   * @returns {Array} querySuggestions of mineral names that matches the payload
   */
  async getQuerySuggestions(context, payload) {
    let reg = new RegExp(`\\b${payload}`, "i");
    const mineralNameArray = await this.state.minerals.mineralNames.slice();
    const querySuggestions = await mineralNameArray.filter((el) =>
      el.match(reg),
    );
    context.commit("getQuerySuggestions", querySuggestions);
  },
  /**
   *
   * @param {String} payload is the mineral name clicked by the user in Search
   * @returns {Object} results comprising one mineral
   */
  async getOneMineral(context, payload) {
    const data = this.state.minerals.mineralDatabase;
    const dictionary = this.state.language.translate;
    const languageId = this.state.language.currentLanguage;

    // if the language is portuguese, use collator to sort
    const collator = new Intl.Collator(languageId, {
      usage: "search",
      sensitivity: "base",
    });
    let match;
    if (languageId === "pt") {
      match = await data.find(
        (dat) => collator.compare(dat[languageId + "_name"], payload) === 0,
      );
    } else {
      match = await data.find(
        (dat) => collator.compare(dat.name, payload) === 0,
      );
    }

    if (!match) {
      context.commit("displayNoMatch", true);
    } else {
      const results = [new MineralObject(languageId, dictionary, match)];
      context.commit("getOneMineral", results);
    }
  },
  /**
   * payload from clicked select options or filter
   * @param {Array} payload of select and option in english, e.g.: ['cleavage', 'perfect'], ['density', 9]
   * @returns {Array} of Objects of Minerals that match the select and option parameters
   */
  async getSelect(context, payload) {
    const res = this.state.minerals.mineralDatabase.slice();
    context.commit("addQuery", payload);

    let queryArray = payload;

    // Adjust some terms
    queryArray = queryArray.map((el) => {
      if (el[1] === "cubic") {
        el[1] = "isometric";
      }
      let sectionName;
      let sectionQuery;
      // let regex;
      if (el[0] === "hardness" || el[0] === "density") {
        sectionQuery = el[1];
      } else {
        sectionQuery = el[1].split(" ")[0];
        sectionQuery = new RegExp(`\\b${sectionQuery}`, "i");
      }

      if (el[0] === "crystal system" || el[0] === "crystal") {
        sectionName = "crystal_system";
      } else {
        sectionName = el[0];
      }

      return [sectionName, sectionQuery];
    });

    const queryMatches = await queryArray.reduce((total, cond) => {
      // hardness and density input refers to the minimum number
      if (cond[0] === "hardness" || cond[0] === "density") {
        total = total.filter((t) => {
          if (
            t[cond[0] + "_min"] === cond[1] ||
            (t[cond[0] + "_min"] > cond[1] && t[cond[0] + "_max"] < cond[1]) ||
            t[cond[0] + "_max"] === cond[1] ||
            (t[cond[0] + "_min"] < cond[1] && t[cond[0] + "_max" === -1])
          ) {
            return t;
          }
        });
        total = total.sort(
          (a, b) =>
            parseFloat(a[cond[0] + "_min"]) - parseFloat(b[cond[0] + "_min"]),
        );
        return total;
      }
      // nommetallic lustre matches all those that are nonmetallic, maybe should be 'transparent' and 'opaque'
      if (cond[0] === "lustre" && cond[1].toString() == "/\\bnonmetallic/i") {
        total = total.filter((t) => {
          let condition = /\bmetallic/i;
          let index = t[cond[0]];
          if (!condition.test(index)) {
            return t;
          }
        });

        return total;
      }

      //filter to find all those that match the conditions

      total = total.filter((t) => {
        if (cond[1].test(t[cond[0]])) return t;
      });

      return total;
    }, res);

    const languageId = this.state.language.currentLanguage;
    const translate = this.state.language.translate;
    // transform the results in object of minerals to be rendered in the user language
    const results = await queryMatches.map(
      (min) => new MineralObject(languageId, translate, min),
    );

    context.commit("getSelect", results);
  },
  /**
   * payload from clicked select options or filter
   * @param {Array} payload of select and option in english, e.g.: ['cleavage', 'perfect'], ['density', 9]
   * @returns {Array} of Objects of Minerals that match the select and option parameters
   */
  async getFilterResults(context, payload) {
    //test

    const res = this.state.minerals.mineralDatabase.slice();
    context.commit("addQuery", payload);

    let queryArray = payload;

    // Adjust some terms
    queryArray = queryArray.map((el) => {
      if (el[1] === "cubic") {
        el[1] = "isometric";
      }
      let sectionName;
      let sectionQuery;
      // let regex;
      if (el[0] === "hardness" || el[0] === "density") {
        sectionQuery = el[1];
      } else {
        sectionQuery = el[1].split(" ")[0];
        sectionQuery = new RegExp(`\\b${sectionQuery}`, "i");
      }

      if (
        el[0] === "crystal system" ||
        el[0] === "crystal" ||
        el[0] === "crystalsystem"
      ) {
        sectionName = "crystal_system";
      } else {
        sectionName = el[0];
      }

      return [sectionName, sectionQuery];
    });

    const queryMatches = await queryArray.reduce((total, cond) => {
      // hardness and density input refers to the minimum number
      if (cond[0] === "hardness" || cond[0] === "density") {
        total = total.filter((t) => {
          if (
            t[cond[0] + "_min"] === cond[1] ||
            (t[cond[0] + "_min"] > cond[1] && t[cond[0] + "_max"] < cond[1]) ||
            t[cond[0] + "_max"] === cond[1] ||
            (t[cond[0] + "_min"] < cond[1] && t[cond[0] + "_max" === -1])
          ) {
            return t;
          }
        });
        total = total.sort(
          (a, b) =>
            parseFloat(a[cond[0] + "_min"]) - parseFloat(b[cond[0] + "_min"]),
        );
        return total;
      }
      // nommetallic lustre matches all those that are nonmetallic, maybe should be 'transparent' and 'opaque'
      if (cond[0] === "lustre" && cond[1].toString() == "/\\bnonmetallic/i") {
        total = total.filter((t) => {
          let condition = /\bmetallic/i;
          let index = t[cond[0]];
          if (!condition.test(index)) {
            return t;
          }
        });

        return total;
      }

      //filter to find all those that match the conditions

      total = total.filter((t) => {
        if (cond[1].test(t[cond[0]])) return t;
      });

      return total;
    }, res);

    if (queryMatches.length === 0) {
      context.commit("getFilterError", true);
    }

    const languageId = this.state.language.currentLanguage;
    const translate = this.state.language.translate;
    // transform the results in object of minerals to be rendered in the user language
    const results = await queryMatches.map(
      (min) => new MineralObject(languageId, translate, min),
    );

    context.commit("getFilterResults", results);
  },
  async getMineralFromParams(context, payload) {
    function removeAccent(str) {
      return str
        .normalize("NFD")
        .replace(/([\u0300-\u036f]|[^a-zA-Z])|\W/g, "")
        .toLowerCase();
    }

    const languageId = this.state.language.currentLanguage;
    const data = this.state.minerals.mineralDatabase;
    const dictionary = this.state.language.translate;

    let match;
    match = await data.filter((d) => removeAccent(d.name) === payload);

    if (match.length === 0) {
      context.commit("displayNoMatch", true);
    }

    if (match.length > 0) {
      const results = new MineralObject(languageId, dictionary, match[0]);
      context.commit("getOnlyOneMineral", results);
      context.commit("displayNoMatch", false);
    }
  },

  /**
   *
   * @param {String} payload is the mineral name clicked by the user in Search
   * @returns {Object} results comprising one mineral
   */
  async getRockMineral(context, payload) {
    const data = this.state.minerals.mineralDatabase;
    const dictionary = this.state.language.translate;
    const languageId = this.state.language.currentLanguage;
    const mineralGroupDatabase = this.state.minerals.mineralGroupDatabase;

    const regex = new RegExp(`\\b${payload}`, "i");

    const filtered = mineralGroupDatabase.filter((mg) => {
      return regex.test(mg.groupId);
    });

    // 1 - Mineral Group && Feldspar
    if (filtered.length > 0 && filtered[0].group_en === "feldspar") {
      const name_Id = "name_" + languageId;
      // 1a) find subgroup
      const subgroupFilter = filtered[0].subgroups.filter(
        (sb) => sb[name_Id] === payload,
      );

      const findSubgroup = filtered[0].subgroups.find(
        (sb) => sb[name_Id] === payload,
      );

      const silicateObject = new SilicateObject(languageId, findSubgroup);
      // 1b) get mineral array
      const minerals = subgroupFilter[0].minerals;

      // 1c) parse
      const parsed = JSON.parse(JSON.stringify(minerals));

      // 1d) get array of mineral objects
      const mineralData = parsed.reduce((acc, cur) => {
        const min = data.filter((d) => d.name === cur);
        if (min.length === 0) return;
        acc.push(min[0]);
        return acc;
      }, []);

      // // 1e) create object
      // const mineralGroup = mineralData.map((m) =>
      //   createMineralObject(languageId, m, dictionary)
      // );
      // console.log(mineralGroup);

      const mineralGroup = mineralData.map(
        (m) => new MineralObject(languageId, dictionary, m),
      );
      // const mineralGroup = parsed.map(
      //   (m) => new MineralObject(languageId, dictionary, m),
      // );
      // console.log(mineralGroup2);

      const rockMineral = {
        mineralGroup: true,
        details: silicateObject,
        minerals: mineralGroup,
      };

      return context.commit("getRockMineral", rockMineral);
    }
    // 2 - Mineral Group !== Feldspar
    if (filtered.length > 0 && filtered[0].group_en !== "feldspar") {
      const minerals = filtered[0]["minerals"];

      const parsed = JSON.parse(JSON.stringify(minerals));

      // 2b) get array of mineral objects
      const mineralData = parsed.reduce((acc, cur) => {
        const min = data.filter((d) => d.name === cur);
        if (min.length === 0) return;
        acc.push(min[0]);
        return acc;
      }, []);

      const silicateObject = new SilicateObject(languageId, filtered[0]);

      // 2c) create object
      // const mineralGroup = mineralData.map((m) =>
      //   createMineralObject(languageId, m, dictionary),
      // );

      const mineralGroup = mineralData.map(
        (m) => new MineralObject(languageId, dictionary, m),
      );

      // const mineralGroup = parsed.map(
      //   (m) => new MineralObject(languageId, data, dictionary, m),
      // );

      const rockMineral = {
        mineralGroup: true,
        details: silicateObject,
        minerals: mineralGroup,
      };

      return context.commit("getRockMineral", rockMineral);
    }

    // 3 - Not Mineral Group

    const match = data.filter((mg) => {
      return languageId === "en" ? regex.test(mg.name) : regex.test(mg.pt_name);
    });

    const results = new MineralObject(languageId, dictionary, match[0]);

    const rockMineral = {
      mineralGroup: false,
      minerals: results,
    };

    context.commit("getRockMineral", rockMineral);
  },
  clearRockMineral(context) {
    return context.commit("getRockMineral", null);
  },
};
